import axios from 'axios';
import mock from 'di-mock';
import { Account, AddressDetails, BalanceSummary, Env, OrderDetails, ProcTransaction,
         Profile, Quote, Rrif, ServerDate, SubTransaction, Tradecount, Option, TradeCommission,
         AvailableFunds, Holdings, TradeFilters, SwitchSecurityList, RecentSymbolList,GoodThroughDates, RewardPoints, LevelTwo, StockOrder, Overview, ClientDetails, NavigationOptions, ProfileClient, OptionsExpirations, MODTokenDetails, ExtTradingHoursUSCA, ExtTradingHours, Features, defaultFeatures, UserEmailPreference, showIntercept, ExchangeAgreement, ExchangeAgreementOPRA, ExchangeAgreementNYSE, ExchangeAgreementNASDAQ, OnboardingStatus, TrancheMetadata, RepaperingRaw, DocumentRequest } from './static/types';
import { FilterParams } from './components/transactions/activity/Activity';
import { OrderFilters } from './components/transactions/orderstatus/OrderStatus';
import { lng } from './i18n';
import { initializeMarkitSession } from './static/markit';
import { ExchangeAgreementEnum } from './static/enums';

const xsrfCookieName = 'RBCXsrf';
const xsrfToken = () => document.cookie.split(';')
  .find((cookie) => cookie.includes(xsrfCookieName))?.trim()
  .slice(`${xsrfCookieName}=`.length);

export { xsrfToken };

export const axiosConfig = {
  xsrfCookieName: null,
  withCredentials: true
};

const api = axios.create(axiosConfig);

const locale = lng === 'en' ? 'English' : 'French';

const setInterceptor = (API_URL) => {
  const getFullUrl = (url, method) =>
    (['get','post'].includes(method) || isISAM ? [API_URL, url] : [API_URL, method, url]).join('/');

  api.interceptors.request.use((config) => {
    config.url = /^https?:\/\//.test(config.url) ? config.url : getFullUrl(config.url, config.method);
    if (config.method !== 'get') {
      config.xsrfCookieName = xsrfCookieName;
      if (config.method === 'delete' && !config.data) config.data = {}; // To resolve issue with RBAccess and post requests
      if(!isISAM) config.url += encodeURIComponent(`?xsrf=${xsrfToken()}`); // Fallback because RBAccess strips the header
      config.method = isISAM ? config.method : 'post';  // Reduce to post (RBAccess crap again, blocks other methods)
    }
    // Only when same origin to prevent CORS preflight
    if (rbaDomain.includes(window.location.host)) {
      config.headers['Locale'] = locale;
      config.headers['Language'] = lng;
    }
    makeRBAccessRequest();
    return config;
  });

  api.interceptors.response.use((response) => {
    return response;
  }, (error) => {
    if (error.response.status === 403) window.location.href = `${rbaUrl}/WebUI/Error/` + (error?.response?.data?.id === 1030 ? 'MultipleSessions' : 'SessionExpiry');
  });
};

type ApiModel = {
  apiStatusCode: number,
  apiResponse: any
};

const sanitize = (data: object | string) => {
  if (typeof data !== 'string' || process.env.NODE_ENV === 'development') return data;
  return data && JSON.parse(data.slice(0, data.length - 1));
};

let rbaUrl, webuiUrl, rbAccessSessionUrl, isISAM, rbaDomain, querySeperator, overrides;

const parseOverrides = (env: Env) =>
  Object.keys(env).filter((k) => k.startsWith('OVERRIDES.')).reduce((acc, k) => ({ ...acc, [k.split('.')[1]]: env[k] }), {});

export const reactBaseMeta: HTMLMetaElement = document.head.querySelector('meta[name="reactBaseURI"]');
const basePath = reactBaseMeta?.content ||
                 document.baseURI ||
                 document.querySelector('base')?.href;
// Force cache bust when CORS
const envParam = /^www1\./i.test(window.location.host) ? process.env.REACT_APP_BUILD_ID : Date.now();
const envFile = process.env.NODE_ENV === 'development' ? 'local.json' : `prod.json?id=${envParam}`;

export const getEnv = (): Promise<Env> =>
  api.get(`${basePath}env/${envFile}`)
    .then((res) => sanitize(res.data))
    .then((env: Env) => {
      isISAM = env.IS_ISAM?.toLowerCase().trim() === 'true' ? true : false;
      querySeperator = isISAM ? '?' : '&';
      if (lng === 'fr') env.RBA_URL = env.RBA_URL.replace('LANGUAGE=ENGLISH', 'LANGUAGE=FRENCH');
      rbAccessSessionUrl = env.RBA_SESSION_URL;
      rbaDomain = env.RBA_DOMAIN;
      rbaUrl = env.RBA_URL;
      webuiUrl = env.WEBUI_URL;
      overrides = parseOverrides(env);
      setInterceptor(env.API_URL);
      initializeMarkitSession(env.MOD_TOKEN_REFETCH_INTERVAL);
      return env;
  });

const parseApiResponse = (data: ApiModel | string, nonModel?: boolean) => {
  if (isISAM) return data;
  const res: ApiModel = sanitize(data);
  if (nonModel) return res;
  if (res.apiStatusCode !== 200) throw new Error();
  return res.apiResponse;
};

export const getNavigationOptions = <T = NavigationOptions>(key, transform?: Function): Promise<T> =>
  api.get(`${webuiUrl}/Navigation/Options`)
    .then((res) => sanitize(res.data))
    .then((opts: NavigationOptions) => transform?.(opts) || opts);

const applyOverrides = (features: Features) =>
  Object.entries(overrides).reduce((acc, [k, v]) => v == null ? acc : { ...acc, [k]: v }, features);

export const getFeatures = <T = Features>(key, transform?: Function): Promise<T> =>
  api.get(`featuretoggleapi/v1/features/${Object.keys(defaultFeatures).join(',')}`)
    .then((res) => parseApiResponse(res.data))
    .then(applyOverrides)
    .then((features: Features) => transform ? transform(features) : features)
    .catch<Features>(() => defaultFeatures);

const toParamString = (paramObj = {}) =>
  Object.keys(paramObj).reduce(
    (string, key) => paramObj[key] ? `${string}${key}=${paramObj[key]}&` : string,
  querySeperator).slice(0, -1);

export const getDate = <T = ServerDate>(key, transform?: Function): Promise<T> =>
  api.get('date')
    .then((res) => parseApiResponse(res.data, true))
    .then((date: ServerDate) => transform?.(date) || date);

export const getAccounts = <T = Account[]>(key, paramObj: object, accToken: string, transform?: Function): Promise<T> =>
  api.get(`portfolioapi/v1/accounts${toParamString(paramObj)}`)
    .then((res) => parseApiResponse(res.data))
    .then((accounts: Account[]) => transform?.(accounts) || accounts);

export const setSticky = (accountToken: string) =>
  api.post(`Preferenceapi/v1/Preferences/sticky`, { accountToken });

export const getMODToken = <T = MODTokenDetails> (): Promise<T> =>
  api.get(`MarkitAPI/v1/markittoken`)
    .then((res) => parseApiResponse(res.data))
    .then((MODTokenDetails) => MODTokenDetails);

export const getTradeCount = <T = Tradecount>(key, accountToken, transform?: Function): Promise<T> =>
  api.get(`portfolioapi/v1/tradecount/accounts/${accountToken}`)
    .then((res) => parseApiResponse(res.data))
    .then((tradecount: Tradecount) => transform?.(tradecount) || tradecount);

export const getBalanceSummary = <T = BalanceSummary>(key, accountToken, transform?: Function): Promise<T> =>
  api.get(`portfolioapi/v1/balances/groups/${accountToken}${querySeperator}timePeriod=IntraDay`)
    .then((res) => parseApiResponse(res.data))
    .then((summary: BalanceSummary) => transform?.(summary) || summary);

export const getSubmitted =
  <T = SubTransaction[]>(key, accountToken, transform?: Function): Promise<T> =>
    api.get(`FundsTransferapi/v1/client/FundTransfer/history/${accountToken}${querySeperator}excludeStatus=Completed&excludeStatus=Processed`)
      .then((res) => parseApiResponse(res.data))
      .then((transactions: SubTransaction[]) => transform?.(transactions) || transactions);

export const cancelTransaction = (referenceId: string) =>
  api.delete(`FundsTransferapi/v1/client/ForeignExchange/${referenceId}`)
    .then((res) => parseApiResponse(res.data));

export const getProcessed =
  <T = ProcTransaction[]>(key, accountToken: string, params: FilterParams, transform?: Function): Promise<T> =>
    api.get(`activityapi/v1/activities/groups/${accountToken}${toParamString(params)}`)
      .then((res) => parseApiResponse(res.data))
      .then((transactions: ProcTransaction[]) => transform?.(transactions) || transactions);

const toOrdersParamString = (paramObj: OrderFilters) =>
  Object.keys(paramObj).every((key) => !paramObj[key]) ? `${querySeperator}orderStatusFilter=None` :
  Object.keys(paramObj).reduce(
    (string, key) => paramObj[key] ? `${string}orderStatusFilter=${key}&` : string,
  querySeperator).slice(0, -1);

export const getOrders =
  <T = OrderDetails>(key, accountToken: string, params: OrderFilters, transform?: Function): Promise<T> =>
    api.get(`orderstatusapi/v1/orderstatus/accounts/${accountToken}${toOrdersParamString(params)}&groupByOrderId=true`)
      .then((res) => parseApiResponse(res.data))
      .then((orderDetails: OrderDetails) => transform?.(orderDetails) || orderDetails);

export const getQuote = <T = Quote>(key, symbol: string, market: string, transform?: Function): Promise<T> =>
  api.get(`MarkitAPI/v1/quote/${market}/${symbol}`)
    .then((res) => parseApiResponse(res.data))
    .then((quote: Quote) => transform?.(quote) || quote);

export const getRrif = <T = Rrif>(key, accountToken: string, isPilot: boolean, transform?: Function): Promise<T> =>
  api.get(`rrifapi/v1/rrif/${accountToken}${isPilot ? `${querySeperator}allowTA=true` : ''}`)
    .then((res) => parseApiResponse(res.data))
    .then((rrif: Rrif) => transform?.(rrif) || rrif);

export const postRrif = (data: object) =>
  api.post('rrifapi/v1/rrif', data)
    .then((res) => parseApiResponse(res.data));

export const getHolidays = <T = Array<string>> (key, transform?: Function): Promise<T> =>
  api.get('rrifapi/v1/rrif/holidays')
    .then((res) => parseApiResponse(res.data))
    .then((date: Array<string>) => transform?.(date) || date);

export const getProfile = <T = Profile> (key, transform?: Function): Promise<T> =>
  api.get('profileapi/v1/profile')
    .then((res) => parseApiResponse(res.data))
    .then((profile: Profile) => transform?.(profile) || profile);

export const getProfileClient = <T = ProfileClient>(key, transform?: Function): Promise<T> =>
  api.get('profileapi/v1/profile/client')
    .then((res) => parseApiResponse(res.data))
    .then((client: T) => transform?.(client) || client);

export const getTrancheMetadata = <T = TrancheMetadata>(key, transform?: Function): Promise<T> =>
  api.get('ePaperingAPI/v1/Tranche/client')
    .then((res) => parseApiResponse(res.data))
    .then((tranche: T) => transform?.(tranche) || tranche);

export const postDocuments = <T = DocumentRequest>(data: string[], transform?: Function): Promise<T> =>
  api.post('documentUploadAPI/v1/Document/RequestID', data)
    .then((res) => parseApiResponse(res.data))
    .then((docReq: T) => transform?.(docReq) || docReq);

export const postDocumentFile = (
  docRequestId: string,
  documentId: string,
  file: File
  )=> {
    const formdata = new FormData();
    formdata.append('FileName',file.name);
    formdata.append('File',file);

    return api.post(`documentUploadAPI/v1/Document/${docRequestId}/document/${documentId}/file`, formdata, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
      .then((res) => parseApiResponse(res.data));
  }

export const deleteDocumentFile = (docRequestId: string, documentId: string, fileId: string) =>
  api.delete(`documentUploadAPI/v1/Document/${docRequestId}/document/${documentId}/file/${fileId}`)
    .then((res) => parseApiResponse(res.data));

export const getRepapering = <T = RepaperingRaw>(key, transform?: Function): Promise<T> =>
  api.get('ePaperingAPI/v1/Repapering')
    .then((res) => parseApiResponse(res.data))
    .then((repapering: T) => transform?.(repapering) || repapering);

export const getMessageCount = <T = number>(key, transform?: Function): Promise<T> =>
  api.get('profileapi/v1/profile/messagecount')
    .then((res) => parseApiResponse(res.data))
    .then((count: T) => transform?.(count) || count);

export const getProfileForCashAndOptions = <T = Profile> (key, requestType?: string, transform?: Function): Promise<T> =>
   api.get('profileapi/v1/profile?inputFlow=' + requestType)
    .then((res) => parseApiResponse(res.data))
    .then((profile: Profile) => transform?.(profile) || profile);

export const getExchangeAgreements = <T = ExchangeAgreement[]>(key, transform?: Function): Promise<T> =>
   api.get('profileapi/v1/Onboarding/ExchangeAgreements')
    .then((res) => parseApiResponse(res.data))
    .then((exchangeAgreements: ExchangeAgreement[]) => transform?.(exchangeAgreements) || exchangeAgreements);

export const postProfile = (data: object) =>
  api.post('profileapi/v1/profile', data)
    .then((res) => parseApiResponse(res.data));

export const postCashAndOptionsProfile = (data: object, requestType: string) =>
    api.post('profileapi/v1/profile/' + requestType, data)
      .then((res) => parseApiResponse(res.data));

export const downloadCashAndOptionsPDF = (data: object, requestType: string) =>
    api.post('profileapi/v1/RenderPDF/' + requestType, data, {responseType: 'blob'})
      .then((res) => parseApiResponse(res.data));

export const postProfileConfirm = () =>
  api.post('profileapi/v1/profile/confirm')
    .then((res) => parseApiResponse(res.data));

export const getAddressSuggestions = (address: string) =>
  api.get(`profileapi/v1/addresslookup/suggestions${querySeperator}address=${address}`)
  .then((res) => parseApiResponse(res.data))
  .then((suggestions: Array<string>) => suggestions);

export const getAddressDetails = (address: string) =>
  api.get(`profileapi/v1/addresslookup/details${querySeperator}address=${address}`)
    .then((res) => parseApiResponse(res.data))
    .then((addressDetails: AddressDetails) => addressDetails);

export const getCountries = <T = Array<Option>> (): Promise<T> =>
  api.get(`profileapi/v1/country`)
    .then((res) => parseApiResponse(res.data));

export const getProvinces = <T = Array<Option>> (): Promise<T> =>
  api.get(`profileapi/v1/province${querySeperator}countrycode=CA`)
    .then((res) => parseApiResponse(res.data));

export const getStates = <T = Array<Option>> (): Promise<T> =>
  api.get(`profileapi/v1/province${querySeperator}countrycode=US`)
    .then((res) => parseApiResponse(res.data));

export const getOccupations = <T = Array<Option>> (): Promise<T> =>
  api.get('profileapi/v1/profile/occupations')
    .then((res) => parseApiResponse(res.data));

export const getEmploymentStatus = <T = Array<Option>> (): Promise<T> =>
  api.get('profileapi/v1/profile/employmentStatus')
    .then((res) => parseApiResponse(res.data));

export const getBusinessTypes = <T = Array<Option>> (): Promise<T> =>
  api.get('profileapi/v1/profile/businesstypes')
    .then((res) => parseApiResponse(res.data))

export const getTradeFilters = <T = TradeFilters> (key, transform?: Function): Promise<T> =>
  api.get(`tradingapi/v1/filters`)
    .then((res) => parseApiResponse(res.data))
    .then((filters) => transform?.(filters) || filters);

export const getAvailableFunds = <T = AvailableFunds> (key, accountToken: string, transform?: Function): Promise<T> =>
  api.get(`availablefundsapi/v1/availablefunds/accounts/${accountToken}/type/Purchase`)
    .then((res) => parseApiResponse(res.data))
    .then((availableFunds) => transform?.(availableFunds) || availableFunds);

export const getHoldings = <T = Holdings> (key, accountToken: string, params: object, transform?: Function): Promise<T> =>
  api.get(`portfolioapi/v1/holdings/accounts/${accountToken}${toParamString(params)}`)
    .then((res) => parseApiResponse(res.data))
    .then((holdings) => transform?.(holdings) || holdings);

export const getTradeCommission = <T = TradeCommission> (key, params: object, transform?: Function): Promise<T> =>
  api.get(`tradingapi/v1/tradecommission${toParamString(params)}`)
    .then((res) => parseApiResponse(res.data))
    .then((tradeCommission) => transform?.(tradeCommission) || tradeCommission);

export const getEstimatedPoints = <T = String> (key, params: object, transform?: Function): Promise<T> =>
  api.get(`tradingapi/v1/tradecommission/points${toParamString(params)}`)
    .then((res) => parseApiResponse(res.data))
    .then((estPoints) => transform?.(estPoints) || estPoints);

export const postMfNew = (data: object, isSwitch?: boolean, practiceAcknowledgement=false) =>
  api.post(`tradingapi/v1/mutualfunds/${isSwitch ? 'switch' : ''}order${practiceAcknowledgement ? '?hideAck=true' : ''}`, data)
    .then((res) => parseApiResponse(res.data));

export const postFundFactAcknowledgement = (accountToken: string, data: object) =>
  api.post(`tradingapi/v1/mutualfunds/fundfacts/acknowledgement/${accountToken}`, data)
    .then((res) => parseApiResponse(res.data));

export const postEQNew = (data: object, practiceAcknowledgement=false) =>
  api.post(`tradingapi/v1/stocks/order${practiceAcknowledgement ? '?hideAck=true' : ''}`, data)
    .then((res) => parseApiResponse(res.data));

export const getSwitchSecurities = <T = SwitchSecurityList> (key, symbol, transform?: Function): Promise<T> =>
  api.get(`tradingapi/v1/mutualfunds/switchtosymbol/${symbol}`)
    .then((res) => parseApiResponse(res.data))
    .then((securityList) => transform?.(securityList) || securityList);

export const getContactPhone = <T = string> (key, transform?: Function): Promise<T> =>
  api.get('Preferenceapi/v1/Preferences/ContactPhone')
    .then((res) => parseApiResponse(res.data))
    .then((phone) => transform?.(phone) || phone);

export const getRecentSymbols = <T = RecentSymbolList[]> (key, transform?: Function): Promise<T> =>
  api.get(`MarkitAPI/v1/RecentSymbols${querySeperator}limit=6&getQuote=true&getChart=true`)
    .then((res) => parseApiResponse(res.data))
    .then((recentSymbolList: RecentSymbolList[]) => transform?.(recentSymbolList) || recentSymbolList);

export const postRecentSymbols = (market, symbol) =>
  api.post(`MarkitAPI/v1/RecentSymbols/${market}/${symbol}`);

export const getPredictiveSearch = <T = RecentSymbolList[]> (key, symbol, transform?: Function): Promise<T> =>
  api.get(`MarkitAPI/v1/predictiveSearch/${encodeURIComponent(symbol)}${querySeperator}limit=6&getQuote=true&getChart=true`)
    .then((res) => parseApiResponse(res.data))
    .then((predictiveSearchList: RecentSymbolList[]) => transform?.(predictiveSearchList) || predictiveSearchList);

export const getGoodThroughDates = <T = GoodThroughDates> (key, transform?: Function): Promise<T> =>
  api.get(`tradingapi/v1/goodthroughdates`)
    .then((res) => parseApiResponse(res.data))
    .then((goodThroughDates: GoodThroughDates) => transform?.(goodThroughDates) || goodThroughDates);

export const getRewardPoints = <T = RewardPoints[]> (key, transform?: Function): Promise<T> =>
  api.get(`portfolioapi/v1/rewardpoints`)
    .then((res) => parseApiResponse(res.data))
    .then((rewardPoints : RewardPoints[]) => transform?.(rewardPoints) || rewardPoints);

export const getLevelTwo = <T = LevelTwo> (key, symbol: string, market: string,transform?: Function): Promise<T> =>
  api.get(`MarkitAPI/v1/detailedQuote/levelTwo/${market}/${symbol}`)
    .then((res) => parseApiResponse(res.data))
    .then((levelTwo : LevelTwo) => transform?.(levelTwo) || levelTwo);

export const getOverview = <T = Overview> (key, type: string, symbol: string, market: string,transform?: Function): Promise<T> =>
  api.get(`MarkitAPI/v1/detailedQuote/overview/${type}/${market}/${symbol}`)
    .then((res) => parseApiResponse(res.data))
    .then((overview: Overview) => transform?.(overview) || overview);

export const getStockOrder = <T = StockOrder> (key, orderId: string, accountToken: string, transform?: Function): Promise<T> =>
  api.get(`tradingapi/v1/stocks/order/${orderId}/accounts/${accountToken}${querySeperator}filter=OpenPending`)
    .then((res) => parseApiResponse(res.data))
    .then((stockOrder : StockOrder) => transform?.(stockOrder) || stockOrder);

export const putModifyOrder = (data: object, orderId: string, practiceAcknowledgement=false) =>
  api.put(`tradingapi/v1/stocks/order/${orderId}${practiceAcknowledgement ? '?hideAck=true' : ''}`, data)
    .then((res) => parseApiResponse(res.data));

export const deleteCancelOrder = (data: object, orderId: string) =>
  api.delete(`tradingapi/v1/stocks/order/${orderId}`, {data})
    .then((res) => parseApiResponse(res.data));

export const getClientDetails = <T = ClientDetails> (key, transform?: Function): Promise<T> =>
  api.get(`portfolioapi/v1/clients`)
    .then((res) => parseApiResponse(res.data))
    .then((clientDetails: ClientDetails) => transform?.(clientDetails) || clientDetails);

export const getOptionsExpirations = <T = OptionsExpirations> (ey, symbol: string, market: string,transform?: Function): Promise<T> =>
api.get(`MarkitAPI/v1/detailedQuote/options/expirations/${market}/${symbol}`)
  .then((res) => parseApiResponse(res.data))
  .then((optionsExpirations: OptionsExpirations) => transform?.(optionsExpirations) || optionsExpirations);

export const getEHTradingWindow = <T = ExtTradingHoursUSCA>(key, transform?: Function): Promise<T> =>
  api.get(`tradingapi/v1/extendedhour/details`)
    .then((res) => parseApiResponse(res.data))
    .then((tradinghours: ExtTradingHoursUSCA) => {
      console.log(transform?.(tradinghours));
      return transform?.(tradinghours) || tradinghours
    });

export const getEHTradingWindowForExchange = <T = ExtTradingHours>(key, exchangeCode: string, transform?: Function): Promise<T> =>
  api.get(`tradingapi/v1/extendedhour/details/${exchangeCode}`)
    .then((res) => parseApiResponse(res.data))
    .then((tradinghours: ExtTradingHours) => {
      console.log(transform?.(tradinghours));
      return transform?.(tradinghours) || tradinghours
    });

export const getShowIntercept = <T = showIntercept> (key, transform?: Function): Promise<T> =>
  api.get('Preferenceapi/v1/EmailPreferences/ShowIntercept')
    .then((res) => parseApiResponse(res.data))
    .then((showIntercept) => transform?.(showIntercept) || showIntercept)

export const getUserEmailPreferences = <T = UserEmailPreference> (key, transform?: Function): Promise<T> =>
  api.get('Preferenceapi/v1/EmailPreferences/UserEmailPreferences')
    .then((res) => parseApiResponse(res.data))
    .then((userEmailPref) => transform?.(userEmailPref) || userEmailPref);

export const confirmEmail = () =>
  api.post('Preferenceapi/v1/EmailPreferences/ConfirmPreference')
    .then((res) => parseApiResponse(res.data));

export const acceptCEG = () =>
  api.post('profileapi/v1/Onboarding/ExchangeAgreementCEG',
    { AgreementType: ExchangeAgreementEnum.CEG, AcceptTermsandConditions: true })
    .then((res) => parseApiResponse(res.data));

export const acceptHK = () =>
  api.post('profileapi/v1/Onboarding/ExchangeAgreementHKG',
    { AgreementType: ExchangeAgreementEnum.HKG, AcceptTermsandConditions: true })
    .then((res) => parseApiResponse(res.data));

export const getOPRA = <T = ExchangeAgreementOPRA>(key, transform?: Function): Promise<T> =>
  api.get('profileapi/v1/Onboarding/ExchangeSignature/OPRA')
    .then((res) => parseApiResponse(res.data))
    .then((agr) => transform?.(agr) || agr);

export const acceptOPRA = (data: object) =>
  api.post('profileapi/v1/Onboarding/ExchangeAgreementOPRA',
    { AgreementType: ExchangeAgreementEnum.OPRA, ...data })
    .then((res) => parseApiResponse(res.data));

export const getNASDAQ = <T = ExchangeAgreementNASDAQ>(key, transform?: Function): Promise<T> =>
  api.get('profileapi/v1/Onboarding/ExchangeSignature/NASDAQ')
    .then((res) => parseApiResponse(res.data))
    .then((agr) => transform?.(agr) || agr);

export const acceptNASDAQ = (data: object) =>
  api.post('profileapi/v1/Onboarding/ExchangeAgreementNASDAQ',
    { AgreementType: ExchangeAgreementEnum.NASDAQ, AcceptTermsandConditions: true, ...data })
    .then((res) => parseApiResponse(res.data));

export const getNYSE = <T = ExchangeAgreementNYSE>(key, transform?: Function): Promise<T> =>
  api.get('profileapi/v1/Onboarding/ExchangeSignature/NYSE')
    .then((res) => parseApiResponse(res.data))
    .then((agr) => transform?.(agr) || agr);

export const acceptNYSE = (data: object) =>
  api.post('profileapi/v1/Onboarding/ExchangeAgreementNYSE',
    { AgreementType: ExchangeAgreementEnum.NYSE, ...data })
    .then((res) => parseApiResponse(res.data));

export const postOnboardingStatus = (data?: object) =>
  api.post('profileapi/v1/Onboarding', data)
    .then((res) => parseApiResponse(res.data));

export const getOnboardingStatus = <T = OnboardingStatus>(key, transform?: Function): Promise<T> =>
api.get('profileapi/v1/Onboarding')
  .then((res) => parseApiResponse(res.data))
  .then((agr) => transform?.(agr) || agr);
/* ISAM/RBAccess logic */

export const makeRBAccessRequest = () => {
  if (isISAM && process.env.NODE_ENV !== 'development') {
    executeHeartbeat();
  }
};

const executeHeartbeat = () =>
  axios.get(rbAccessSessionUrl, {
      timeout: 30000
    })
    .then((res) => {
      handleRequestTimeout(res);
      return res.data;
    })
    .catch(handleRequestError);

const handleRequestTimeout = (res) => {
  if (res !== undefined) {
    const isIsamHtml =  typeof res.data === 'string' && res.data.includes('/cgi-bin/rbaccess/rbunxcgi?F22=HTSSOINET');
    // Some weird response. Refresh to try re-establishing session
    if (isIsamHtml) {
      window.location.reload();
      return;
    }

    const didUserSignOut = res.data && res.data.operation && res.data.operation === 'login';

    // Isam timed out or user signed out
    if (didUserSignOut) {
      terminateSession();
      return;
    }
  }
};

const handleRequestError = (err) => {
  if (err.response?.status === 401) {
    terminateSession();
  }
};

const terminateSession = () => {
  let logoutPath = rbaDomain + '/' + locale.toLowerCase() + '/netaction/expmsg.htm';
  window.location.href = logoutPath;
};
